import React from 'react';
import { toast } from 'react-toastify';

import { CopyIcon } from '../../assets/CopyIcon';
import { LinkIcon } from '../../assets/LinkIcon';
import { BOTTOM_TOAST } from '../../utils';

export type OnCallDashboardResponse = {
  count: number;
  results: Record<string, unknown>[];
  last_db_fetch_at: string;
  sop?: string;
};

interface TileContentProps {
  data: OnCallDashboardResponse;
}

export const TileContent = ({ data }: TileContentProps) => {
  const handleCopy = (text: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigator.clipboard.writeText(text);
    toast.info('Copied to clipboard', BOTTOM_TOAST);
  };

  const handleCustomerLookup = (accountId: string) => {
    window.open(`/customer-lookup/${accountId}`, '_blank');
  };

  const isAccountIdCell = (key: string) => key === 'account_id';

  const getCellClassName = (key: string) => {
    const baseClasses =
      'group relative px-4 py-1 pr-8 whitespace-nowrap cursor-pointer hover:bg-slate-50';
    const textColor = isAccountIdCell(key) ? 'text-anchor' : 'text-slate-700';
    return `${baseClasses} ${textColor}`;
  };

  const getCellClickHandler = (key: string, cellValue: string) => {
    return (e: React.MouseEvent) => {
      if (isAccountIdCell(key)) {
        handleCustomerLookup(cellValue);
      } else {
        handleCopy(cellValue, e);
      }
    };
  };

  const getButtonIcon = (key: string) => {
    return isAccountIdCell(key) ? (
      <LinkIcon className="w-3 h-3 text-slate-600" />
    ) : (
      <CopyIcon className="w-3 h-3 text-slate-600" />
    );
  };

  const getButtonTitle = (key: string) => {
    return isAccountIdCell(key) ? 'Open account in new tab' : 'Copy to clipboard';
  };

  if (data.results.length === 0) {
    return (
      <div className="flex h-full min-h-[200px] items-center justify-center text-[16pt]">
        No data
      </div>
    );
  }

  return (
    <table className="min-w-full text-[8pt]">
      <thead>
        <tr className="border-b border-slate-200">
          <th className="sticky left-0 z-10 bg-white px-4 py-1 text-left font-medium text-slate-600 whitespace-nowrap shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
            #
          </th>
          {Object.keys(data.results[0]).map((key) => (
            <th
              key={key}
              className="px-4 py-1 text-left font-medium text-slate-600 whitespace-nowrap"
            >
              {key}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {data.results.map((item, index) => {
          const rowNumber = String(index + 1);
          return (
            <tr key={index} className="border-b border-slate-100">
              <td className="sticky left-0 z-10 bg-white group px-4 py-1 text-slate-700 whitespace-nowrap shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                <span>{rowNumber}</span>
              </td>
              {Object.keys(item).map((key) => {
                const cellValue = String(item[key]);
                return (
                  <td
                    key={key}
                    className={getCellClassName(key)}
                    onClick={getCellClickHandler(key, cellValue)}
                  >
                    <span>{cellValue}</span>
                    <button
                      className="absolute right-1 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-slate-200 rounded"
                      onClick={getCellClickHandler(key, cellValue)}
                      title={getButtonTitle(key)}
                    >
                      {getButtonIcon(key)}
                    </button>
                  </td>
                );
              })}
            </tr>
          );
        })}
      </tbody>
    </table>
  );
};
